import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Replace } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SCAN_CLEANABLE_EXT = [
  ".jpg", ".jpeg", ".png", ".webp", ".gif", ".tif", ".tiff",
  ".heic", ".heif", ".bmp", ".pdf",
];

export function isScanCleanable(doc) {
  if (!doc) return false;
  let ext = (doc.file_ext || "").toLowerCase();
  if (ext && !ext.startsWith(".")) ext = `.${ext}`;
  if (!ext && doc.file_name && doc.file_name.includes(".")) {
    ext = `.${doc.file_name.split(".").pop().toLowerCase()}`;
  }
  if (SCAN_CLEANABLE_EXT.includes(ext)) return true;
  const mime = (doc.file_type || "").toLowerCase();
  return mime.startsWith("image/") || mime === "application/pdf";
}

export default function PulisciScansioneDialog({
  open,
  onOpenChange,
  fascicoloId,
  token,
  documento,
  onApplied,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usaAi, setUsaAi] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setUsaAi(true);
      setResult(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !documento?.file_id || !fascicoloId) {
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setResult(null);
      try {
        const { data } = await axios.post(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/documenti/${documento.file_id}/pulisci-scansione`,
          { preview: true, usa_ai: usaAi },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
        );
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Impossibile pulire la scansione");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, documento?.file_id, fascicoloId, token, usaAi]);

  const apply = async ({ replace }) => {
    if (!documento?.file_id) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/documenti/${documento.file_id}/pulisci-scansione`,
        { preview: false, replace, save_copy: !replace, usa_ai: usaAi },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      toast.success(replace ? "Scansione sostituita con la versione pulita" : "Salvata una copia pulita del documento");
      onApplied?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const methodLabel = {
    crop: "Ritaglio automatico dello sfondo",
    prospettiva: "Correzione prospettiva e ritaglio",
    nessun_ritaglio: "Contrasto e raddrizzamento (sfondo già minimo)",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Pulisci scansione
          </DialogTitle>
          <DialogDescription>
            Ritaglia lo sfondo della foto, raddrizza il foglio e migliora contrasto e nitidezza, come in una scansione Adobe.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm font-medium text-slate-800 truncate">{documento?.file_name}</p>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin text-violet-500 mb-3" />
            <p className="text-sm">Analisi e pulizia in corso…</p>
            <p className="text-xs mt-1">Su foto con tavolo o pavimento può richiedere qualche secondo.</p>
          </div>
        ) : result ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5">Originale</p>
                <div className="rounded-lg border bg-slate-100 overflow-hidden flex items-center justify-center min-h-[180px]">
                  <img
                    alt="Originale"
                    className="max-h-64 max-w-full object-contain"
                    src={`data:image/jpeg;base64,${result.original_preview_jpeg_base64}`}
                  />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-emerald-700 mb-1.5">Pulita</p>
                <div className="rounded-lg border border-emerald-200 bg-white overflow-hidden flex items-center justify-center min-h-[180px]">
                  <img
                    alt="Scansione pulita"
                    className="max-h-64 max-w-full object-contain"
                    src={`data:image/jpeg;base64,${result.preview_jpeg_base64}`}
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              {methodLabel[result.crop_method] || result.crop_method}
              {result.deskew_angle ? ` · raddrizzamento ${result.deskew_angle}°` : ""}
              {result.pages > 1 ? ` · ${result.pages} pagine` : ""}
              {result.ai_used ? " · bordi rilevati con AI" : ""}
            </p>
            {(result.warnings || []).length > 0 && (
              <p className="text-xs text-amber-700">{result.warnings.join(" · ")}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center">
            Nessuna anteprima. Prova a togliere l&apos;opzione AI e riprovare, oppure chiudi e riapri.
          </p>
        )}

        <label className="flex items-start gap-2 text-sm text-slate-700">
          <Checkbox checked={usaAi} onCheckedChange={(v) => setUsaAi(!!v)} className="mt-0.5" disabled={loading || saving} />
          <span>
            Se lo sfondo non si stacca da solo, usa l&apos;AI per trovare i bordi del foglio
            (consigliato per foto di tessera o CI sul tavolo).
          </span>
        </label>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button
            variant="outline"
            className="border-violet-200 text-violet-700"
            onClick={() => apply({ replace: false })}
            disabled={loading || saving || !result}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Copy className="w-4 h-4 mr-1.5" />}
            Salva copia
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => apply({ replace: true })}
            disabled={loading || saving || !result}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Replace className="w-4 h-4 mr-1.5" />}
            Sostituisci originale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
